/**
 * Domain enums shared by the API and the web client.
 *
 * Every member here must stay in sync with the matching Prisma enum in
 * `api/prisma/schema.prisma`. Because these are plain frozen objects they can
 * be iterated at runtime to build filter dropdowns and validated against by
 * Zod (`z.enum(Object.values(OrderStatus))`).
 */

export const Role = Object.freeze({
  USER: 'USER',
  SUPPORT: 'SUPPORT',
  ADMIN: 'ADMIN',
  SUPER_ADMIN: 'SUPER_ADMIN',
})

/** Ascending privilege, used by `hasAtLeast(role, minimum)`. */
export const ROLE_RANK = Object.freeze({
  USER: 0,
  SUPPORT: 1,
  ADMIN: 2,
  SUPER_ADMIN: 3,
})

/** True when `role` is at least as privileged as `minimum`. */
export function hasAtLeast(role, minimum) {
  return (ROLE_RANK[role] ?? -1) >= (ROLE_RANK[minimum] ?? Infinity)
}

/** Any role that can reach the admin area at all. */
export const STAFF_ROLES = Object.freeze([Role.SUPPORT, Role.ADMIN, Role.SUPER_ADMIN])

export function isStaff(role) {
  return STAFF_ROLES.includes(role)
}

export const UserStatus = Object.freeze({
  ACTIVE: 'ACTIVE',
  SUSPENDED: 'SUSPENDED',
  BANNED: 'BANNED',
  PENDING_VERIFICATION: 'PENDING_VERIFICATION',
})

/**
 * Which of the two interfaces a customer is served.
 *
 * One account, one wallet, one catalogue, one ordering engine — presented two
 * ways. NORMAL guides someone through their first purchase; PRO is the dense
 * surface a reseller placing forty orders a day expects. Never a permission
 * level: switching modes changes nothing about what an account may do.
 */
export const InterfaceMode = Object.freeze({
  NORMAL: 'NORMAL',
  PRO: 'PRO',
})

/** The mode every account starts in, and the fallback for any unknown value. */
export const DEFAULT_INTERFACE_MODE = InterfaceMode.NORMAL

/** Coerces anything — a stale cache, a hand-edited payload — to a valid mode. */
export function toInterfaceMode(value) {
  return value === InterfaceMode.PRO ? InterfaceMode.PRO : DEFAULT_INTERFACE_MODE
}

/**
 * Our internal order lifecycle. Deliberately independent of any upstream
 * provider vocabulary — provider strings are normalised into these values by
 * the provider adapter, never stored raw as our status.
 */
export const OrderStatus = Object.freeze({
  PENDING: 'PENDING',
  QUEUED: 'QUEUED',
  SUBMITTING: 'SUBMITTING',
  PROCESSING: 'PROCESSING',
  IN_PROGRESS: 'IN_PROGRESS',
  PARTIAL: 'PARTIAL',
  COMPLETED: 'COMPLETED',
  CANCELED: 'CANCELED',
  FAILED: 'FAILED',
  REFUNDED: 'REFUNDED',
})

/** Statuses that will never change again — safe to stop polling the provider. */
export const TERMINAL_ORDER_STATUSES = Object.freeze([
  OrderStatus.COMPLETED,
  OrderStatus.CANCELED,
  OrderStatus.FAILED,
  OrderStatus.REFUNDED,
])

/** Statuses that still count as "in flight" for dashboard counters. */
export const ACTIVE_ORDER_STATUSES = Object.freeze([
  OrderStatus.PENDING,
  OrderStatus.QUEUED,
  OrderStatus.SUBMITTING,
  OrderStatus.PROCESSING,
  OrderStatus.IN_PROGRESS,
])

export function isTerminalOrderStatus(status) {
  return TERMINAL_ORDER_STATUSES.includes(status)
}

export function isActiveOrderStatus(status) {
  return ACTIVE_ORDER_STATUSES.includes(status)
}

/** Drives the colour of the status pill in the UI. */
export const ORDER_STATUS_TONE = Object.freeze({
  PENDING: 'neutral',
  QUEUED: 'neutral',
  SUBMITTING: 'info',
  PROCESSING: 'info',
  IN_PROGRESS: 'info',
  PARTIAL: 'warning',
  COMPLETED: 'success',
  CANCELED: 'neutral',
  FAILED: 'error',
  REFUNDED: 'violet',
})

/** Customer-facing wording; the raw enum name is never shown in the panel. */
export const ORDER_STATUS_LABEL = Object.freeze({
  PENDING: 'Pending',
  QUEUED: 'Queued',
  SUBMITTING: 'Submitting',
  PROCESSING: 'Processing',
  IN_PROGRESS: 'In progress',
  PARTIAL: 'Partial',
  COMPLETED: 'Completed',
  CANCELED: 'Canceled',
  FAILED: 'Failed',
  REFUNDED: 'Refunded',
})

export const TransactionType = Object.freeze({
  DEPOSIT: 'DEPOSIT',
  ORDER: 'ORDER',
  REFUND: 'REFUND',
  ADJUSTMENT: 'ADJUSTMENT',
  PROMOTION: 'PROMOTION',
  CHARGEBACK: 'CHARGEBACK',
})

export const TRANSACTION_TYPE_LABEL = Object.freeze({
  DEPOSIT: 'Deposit',
  ORDER: 'Order',
  REFUND: 'Refund',
  ADJUSTMENT: 'Adjustment',
  PROMOTION: 'Promotion',
  CHARGEBACK: 'Chargeback',
})

export const TransactionStatus = Object.freeze({
  PENDING: 'PENDING',
  COMPLETED: 'COMPLETED',
  FAILED: 'FAILED',
  REVERSED: 'REVERSED',
})

export const PaymentProvider = Object.freeze({
  STRIPE: 'STRIPE',
  XENDIT: 'XENDIT',
  NOWPAYMENTS: 'NOWPAYMENTS',
  MANUAL: 'MANUAL',
})

export const PaymentStatus = Object.freeze({
  PENDING: 'PENDING',
  PROCESSING: 'PROCESSING',
  PAID: 'PAID',
  FAILED: 'FAILED',
  EXPIRED: 'EXPIRED',
  REFUNDED: 'REFUNDED',
  PARTIALLY_REFUNDED: 'PARTIALLY_REFUNDED',
  CHARGEBACK: 'CHARGEBACK',
})

export const PAYMENT_STATUS_TONE = Object.freeze({
  PENDING: 'neutral',
  PROCESSING: 'info',
  PAID: 'success',
  FAILED: 'error',
  EXPIRED: 'neutral',
  REFUNDED: 'violet',
  PARTIALLY_REFUNDED: 'warning',
  CHARGEBACK: 'error',
})

/** Payment states after which the wallet must never be credited again. */
export const TERMINAL_PAYMENT_STATUSES = Object.freeze([
  PaymentStatus.PAID,
  PaymentStatus.FAILED,
  PaymentStatus.EXPIRED,
  PaymentStatus.REFUNDED,
  PaymentStatus.CHARGEBACK,
])

/**
 * How a customer-facing price is derived from the provider cost.
 * Resolution order is service -> category -> provider -> global.
 */
export const PricingMode = Object.freeze({
  FIXED: 'FIXED',
  PERCENTAGE_MARKUP: 'PERCENTAGE_MARKUP',
  GLOBAL_MARKUP: 'GLOBAL_MARKUP',
  CATEGORY_MARKUP: 'CATEGORY_MARKUP',
  SERVICE_MARKUP: 'SERVICE_MARKUP',
})

/** Which rule actually produced the final price — surfaced in admin tooling. */
export const PriceSource = Object.freeze({
  SERVICE_FIXED: 'SERVICE_FIXED',
  SERVICE_MARKUP: 'SERVICE_MARKUP',
  CATEGORY_MARKUP: 'CATEGORY_MARKUP',
  PROVIDER_MARKUP: 'PROVIDER_MARKUP',
  GLOBAL_MARKUP: 'GLOBAL_MARKUP',
  /// A regional tier undercut whatever the standard rules produced.
  REGIONAL_TIER: 'REGIONAL_TIER',
})

export const PRICE_SOURCE_LABEL = Object.freeze({
  SERVICE_FIXED: 'Service fixed price',
  SERVICE_MARKUP: 'Service markup',
  CATEGORY_MARKUP: 'Category markup',
  PROVIDER_MARKUP: 'Provider markup',
  REGIONAL_TIER: 'Regional pricing tier',
  GLOBAL_MARKUP: 'Global markup',
})

export const ProviderStatus = Object.freeze({
  ACTIVE: 'ACTIVE',
  DISABLED: 'DISABLED',
  DEGRADED: 'DEGRADED',
})

export const TicketStatus = Object.freeze({
  OPEN: 'OPEN',
  PENDING_SUPPORT: 'PENDING_SUPPORT',
  PENDING_CUSTOMER: 'PENDING_CUSTOMER',
  RESOLVED: 'RESOLVED',
  CLOSED: 'CLOSED',
})

export const OPEN_TICKET_STATUSES = Object.freeze([
  TicketStatus.OPEN,
  TicketStatus.PENDING_SUPPORT,
  TicketStatus.PENDING_CUSTOMER,
])

export const TICKET_STATUS_LABEL = Object.freeze({
  OPEN: 'Open',
  PENDING_SUPPORT: 'Awaiting support',
  PENDING_CUSTOMER: 'Awaiting your reply',
  RESOLVED: 'Resolved',
  CLOSED: 'Closed',
})

export const TICKET_STATUS_TONE = Object.freeze({
  OPEN: 'info',
  PENDING_SUPPORT: 'warning',
  PENDING_CUSTOMER: 'violet',
  RESOLVED: 'success',
  CLOSED: 'neutral',
})

export const TicketPriority = Object.freeze({
  LOW: 'LOW',
  NORMAL: 'NORMAL',
  HIGH: 'HIGH',
  URGENT: 'URGENT',
})

export const TicketCategory = Object.freeze({
  ORDER: 'ORDER',
  PAYMENT: 'PAYMENT',
  ACCOUNT: 'ACCOUNT',
  REFILL: 'REFILL',
  OTHER: 'OTHER',
})

/** Shape of the value a service expects in the "link" field of a new order. */
export const ServiceInputType = Object.freeze({
  URL: 'URL',
  USERNAME: 'USERNAME',
  MEDIA_URL: 'MEDIA_URL',
  CHANNEL_URL: 'CHANNEL_URL',
  CUSTOM: 'CUSTOM',
})

export const SERVICE_INPUT_LABEL = Object.freeze({
  URL: 'Link',
  USERNAME: 'Username',
  MEDIA_URL: 'Post link',
  CHANNEL_URL: 'Channel link',
  CUSTOM: 'Value',
})

export const JobStatus = Object.freeze({
  PENDING: 'PENDING',
  RUNNING: 'RUNNING',
  COMPLETED: 'COMPLETED',
  FAILED: 'FAILED',
  DEAD: 'DEAD',
})

export const JobType = Object.freeze({
  SUBMIT_ORDER: 'SUBMIT_ORDER',
  SYNC_ORDER_STATUS: 'SYNC_ORDER_STATUS',
  SYNC_PROVIDER_SERVICES: 'SYNC_PROVIDER_SERVICES',
  REFILL_ORDER: 'REFILL_ORDER',
  CANCEL_ORDER: 'CANCEL_ORDER',
  CHECK_PROVIDER_BALANCE: 'CHECK_PROVIDER_BALANCE',
})

export const RefillStatus = Object.freeze({
  REQUESTED: 'REQUESTED',
  SUBMITTED: 'SUBMITTED',
  IN_PROGRESS: 'IN_PROGRESS',
  COMPLETED: 'COMPLETED',
  REJECTED: 'REJECTED',
  FAILED: 'FAILED',
})

/** Convenience for Zod: `z.enum(values(OrderStatus))`. */
export function values(enumObject) {
  return Object.values(enumObject)
}
